import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Gavel as GavelIcon,
  LocationOn as LocationIcon,
  Computer as ComputerIcon,
  Language as LanguageIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import {
  purposeService,
  PurposeHistoryResponse,
  PurposeHistoryItem,
  Purpose,
  WithdrawalHistoryResponse,
  WithdrawalHistoryItem,
} from "../services/purpose-service";
import WithdrawalConfirmationDialog from "../components/WithdrawalConfirmationDialog";
import WithdrawalStatusChip from "../components/WithdrawalStatusChip";
import {
  initPDFDocument,
  addText,
  addSectionHeader,
  addKeyValue,
  addDivider,
  addBadge,
  addFooter,
  savePDF,
  checkPageBreak,
} from "../utils/pdf-export";

interface PurposeDetailPageProps {
  jwt: string;
  clientData: {
    subdomain: string;
    clientName: string;
  } | null;
}

const PurposeDetailPage: React.FC<PurposeDetailPageProps> = ({ jwt, clientData }) => {
  const { purposeId } = useParams<{ purposeId: string }>();
  const navigate = useNavigate();

  // State management
  const [purposeHistory, setPurposeHistory] = useState<PurposeHistoryResponse | null>(null);
  const [withdrawalHistory, setWithdrawalHistory] = useState<WithdrawalHistoryResponse | null>(null);
  const [purposeDetails, setPurposeDetails] = useState<Purpose | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Withdrawal dialog state
  const [withdrawalDialog, setWithdrawalDialog] = useState<{
    open: boolean;
    purpose: Purpose | null;
    loading: boolean;
    error: string | null;
  }>({
    open: false,
    purpose: null,
    loading: false,
    error: null,
  });

  // Load purpose details and history on component mount
  useEffect(() => {
    const loadPurposeData = async () => {
      if (!purposeId) {
        setError("Purpose ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch purpose details, consent history, and withdrawal history in parallel
        const [purposesData, historyData, withdrawalHistoryData] = await Promise.all([
          purposeService.getPurposes(false), // Get all purposes to find the specific one
          purposeService.getPurposeHistory(parseInt(purposeId)),
          purposeService.getPurposeWithdrawalHistory(parseInt(purposeId)).catch((err) => {
            // Don't fail entire load if withdrawal history fails
            console.error("Failed to load withdrawal history:", err);
            return { success: true, data: [] };
          }),
        ]);

        // Find the specific purpose from the purposes list
        const purpose = purposesData.find((p) => p.purposeId === parseInt(purposeId));
        if (!purpose) {
          setError("Purpose not found");
          return;
        }

        setPurposeDetails(purpose);
        setPurposeHistory(historyData);
        setWithdrawalHistory(withdrawalHistoryData);
      } catch (err: any) {
        setError(err.message || "Failed to load purpose data");
      } finally {
        setLoading(false);
      }
    };

    if (jwt && purposeId) {
      loadPurposeData();
    }
  }, [jwt, purposeId]);

  const handleBackClick = () => {
    navigate("/purposes");
  };

  const handleWithdrawClick = () => {
    if (purposeDetails) {
      setWithdrawalDialog({
        open: true,
        purpose: purposeDetails,
        loading: false,
        error: null,
      });
    }
  };

  const handleWithdrawalConfirm = async (reason?: string) => {
    if (!withdrawalDialog.purpose) return;

    setWithdrawalDialog((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await purposeService.createWithdrawalRequest(withdrawalDialog.purpose.purposeId, reason);

      // Close dialog
      setWithdrawalDialog({
        open: false,
        purpose: null,
        loading: false,
        error: null,
      });

      // Reload purpose details to show updated status
      const purposesData = await purposeService.getPurposes(false);
      const updatedPurpose = purposesData.find((p) => p.purposeId === withdrawalDialog.purpose!.purposeId);
      if (updatedPurpose) {
        setPurposeDetails(updatedPurpose);
      }

      console.log("Withdrawal request created successfully:", response);
    } catch (err: any) {
      setWithdrawalDialog((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Failed to create withdrawal request",
      }));
    }
  };

  const handleWithdrawalClose = () => {
    setWithdrawalDialog({
      open: false,
      purpose: null,
      loading: false,
      error: null,
    });
  };

  const handleViewWithdrawalDetails = () => {
    if (purposeDetails?.withdrawalRequest) {
      console.log("View withdrawal details for purpose:", purposeId, purposeDetails.withdrawalRequest);
    }
  };

  const handleGrantClick = () => {
    // TODO: Implement grant functionality
    console.log("Grant clicked for purpose:", purposeId);
  };

  const handleExportHistory = () => {
    if (!purposeDetails || !purposeHistory) return;

    const pdf = initPDFDocument("Purpose Consent History Report");

    // Purpose Name at the top
    pdf.yPosition = 35; // Move below title
    addText(pdf, purpose.purposeName, 16, true, "#1B5E20");
    pdf.yPosition += 8;

    // Summary Information Box
    addText(pdf, "Report Summary", 12, true, "#2E7D32");
    pdf.yPosition += 5;

    // Calculate summary box height based on content
    const summaryBoxY = pdf.yPosition;
    const boxPadding = 8;
    const lineSpacing = 7;
    const summaryContentHeight = lineSpacing * 3 + boxPadding * 2;

    // Add summary box border first
    pdf.doc.setDrawColor("#2E7D32");
    pdf.doc.setLineWidth(0.5);
    pdf.doc.setFillColor("#F1F8F4");
    pdf.doc.roundedRect(pdf.margin, summaryBoxY, pdf.pageWidth - 2 * pdf.margin, summaryContentHeight, 2, 2, "FD");

    // Summary content inside box - use direct text placement to avoid pagination
    const summaryStartY = summaryBoxY + boxPadding;
    pdf.yPosition = summaryStartY;

    // Generated date
    pdf.doc.setFontSize(9);
    pdf.doc.setFont("helvetica", "normal");
    pdf.doc.setTextColor("#333333");
    pdf.doc.text(`Generated: ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`, pdf.margin + 5, pdf.yPosition);
    pdf.yPosition += lineSpacing;

    // Summary stats
    const consentCount = history.length;
    const withdrawalCount = withdrawalHistory?.data?.length || 0;
    pdf.doc.text(`Total Consents: ${consentCount} | Withdrawal Requests: ${withdrawalCount}`, pdf.margin + 5, pdf.yPosition);
    pdf.yPosition += lineSpacing;

    // Current status
    pdf.doc.text(
      `Current Status: ${purpose.status.replace(/_/g, " ")} | Current Consent: ${purpose.currentConsent ? "Granted" : "Withdrawn"}`,
      pdf.margin + 5,
      pdf.yPosition
    );

    // Reset font size
    pdf.doc.setFontSize(10);

    // Position after box
    pdf.yPosition = summaryBoxY + summaryContentHeight + 8;

    // Purpose Information Section
    addSectionHeader(pdf, "Purpose Information");
    addKeyValue(pdf, "Purpose Key", purpose.purposeKey);
    addKeyValue(pdf, "Description", purpose.purposeDescription || "N/A");
    addKeyValue(pdf, "Lawful Ground", purpose.lawfulGroundLabel);
    addKeyValue(pdf, "Last Consent Date", formatDate(purpose.lastConsentDate));

    if (purpose.retentionExpirationDate) {
      addKeyValue(pdf, "Retention Expires", formatDate(purpose.retentionExpirationDate));
    }

    if (purpose.legalRetentionRequired) {
      pdf.yPosition += 3;
      addText(pdf, "Legal Retention Required", 10, true, "#2E7D32");
      if (purpose.legalRetentionPeriod) {
        addKeyValue(pdf, "Retention Period", `${purpose.legalRetentionPeriod} days`);
      }
    }

    checkPageBreak(pdf, 80);

    // Consent History Section
    if (history.length === 0) {
      addSectionHeader(pdf, "Consent History");
      addText(pdf, "No consent history found for this purpose.", 10, false, "#666666");
    } else {
      addSectionHeader(pdf, `Consent History (${history.length} entries)`);

      history.forEach((item, index) => {
        checkPageBreak(pdf, 60);

        // Entry header
        addText(pdf, `Entry ${index + 1}`, 12, true, "#1B5E20");

        // Consent status
        const statusText = item.consentValue ? "Consent Granted" : "Consent Withdrawn";
        addText(pdf, statusText, 11, true);

        // Sandbox badge on same line or next line if needed
        if (item.isTest) {
          pdf.doc.setFontSize(11); // Set font size to match status text for width calculation
          const statusWidth = pdf.doc.getTextWidth(statusText);
          pdf.doc.setFontSize(10); // Reset

          const badgeTextWidth = 45; // Approximate width for "SANDBOX" at 8px font
          const badgeWidth = badgeTextWidth + 8; // 8px padding
          const availableWidth = pdf.pageWidth - pdf.margin - (pdf.margin + statusWidth + 15);

          if (availableWidth > badgeWidth) {
            // Place badge on same line, to the right
            const badgeX = pdf.margin + statusWidth + 15;
            const badgeY = pdf.yPosition - 6;
            pdf.doc.setFillColor("#FFF3CD");
            pdf.doc.roundedRect(badgeX, badgeY, badgeWidth, 6, 2, 2, "F");
            pdf.doc.setFontSize(8);
            pdf.doc.setFont("helvetica", "bold");
            pdf.doc.setTextColor("#856404");
            pdf.doc.text("SANDBOX", badgeX + 4, badgeY + 4);
            pdf.doc.setFontSize(10); // Reset font size
            pdf.doc.setFont("helvetica", "normal"); // Reset font style
          } else {
            // Place badge on next line
            pdf.yPosition += pdf.lineHeight;
            addBadge(pdf, "SANDBOX", "#FFF3CD", "#856404");
            pdf.yPosition -= 3; // Adjust spacing
          }
        }

        pdf.yPosition += 5;
        addKeyValue(pdf, "Consent Date", formatDate(item.consentDate));

        // Collection Point
        addKeyValue(pdf, "Collection Point", item.collectionPoint.name);
        addText(pdf, `  Type: ${item.collectionPoint.type} • ID: ${item.collectionPoint.identifier}`, 9, false, "#666666");

        // Location
        if (item.location) {
          const locationText = `${item.location.city || 'N/A'}, ${item.location.region || 'N/A'}, ${item.location.country || 'N/A'}`;
          addKeyValue(pdf, "Location", locationText);
        }
        addKeyValue(pdf, "IP Address", item.ipAddress || 'N/A');

        // Language
        addKeyValue(pdf, "Language", item.languageCode.toUpperCase());

        // User Agent (truncated more aggressively to prevent pagination issues)
        const maxUserAgentLength = 55; // Reduced to prevent wrapping issues
        const userAgentText = item.userAgent.length > maxUserAgentLength ? item.userAgent.substring(0, maxUserAgentLength) + "..." : item.userAgent;
        addKeyValue(pdf, "User Agent", userAgentText);

        // Retention expiration
        if (item.retentionExpirationDate) {
          addKeyValue(pdf, "Retention Expires", formatDate(item.retentionExpirationDate));
        }

        // Source
        addKeyValue(pdf, "Source", item.consentSource);
        if ((item.consentSource === "NOTICE" || item.consentSource === "NOTICE_OTP") && item.isMinor) {
          addKeyValue(pdf, "Provenance", "Guardian on behalf of minor");
        }

        // Add divider between entries (except last)
        if (index < history.length - 1) {
          addDivider(pdf);
        }
      });
    }

    // Withdrawal Request History Section
    if (withdrawalHistory?.data && withdrawalHistory.data.length > 0) {
      checkPageBreak(pdf, 80);
      addSectionHeader(pdf, `Withdrawal Request History (${withdrawalHistory.data.length} request(s))`);

      withdrawalHistory.data.forEach((item, index) => {
        checkPageBreak(pdf, 50);

        // Request header
        const requestText = `Request #${item.requestId}`;
        addText(pdf, requestText, 12, true, "#1B5E20");

        // Status badge - place on same line if possible
        const statusColor =
          item.requestStatus === "COMPLETED"
            ? "#4CAF50"
            : item.requestStatus === "CANCELLED"
            ? "#9E9E9E"
            : item.requestStatus === "REJECTED"
            ? "#F44336"
            : "#FF9800";
        const bgColor =
          item.requestStatus === "COMPLETED"
            ? "#E8F5E9"
            : item.requestStatus === "CANCELLED"
            ? "#FAFAFA"
            : item.requestStatus === "REJECTED"
            ? "#FFEBEE"
            : "#FFF3E0";

        // Calculate badge position similar to Sandbox badge
        pdf.doc.setFontSize(12); // Set font size to match request text for width calculation
        const requestWidth = pdf.doc.getTextWidth(requestText);
        pdf.doc.setFontSize(10); // Reset

        const statusText = item.requestStatus;
        const badgeTextWidth = pdf.doc.getTextWidth(statusText);
        const badgeWidth = badgeTextWidth + 8; // 8px padding
        const availableWidth = pdf.pageWidth - pdf.margin - (pdf.margin + requestWidth + 15);

        if (availableWidth > badgeWidth) {
          // Place badge on same line, to the right
          const badgeX = pdf.margin + requestWidth + 15;
          const badgeY = pdf.yPosition - 8; // Adjust Y position
          pdf.doc.setFillColor(bgColor);
          pdf.doc.roundedRect(badgeX, badgeY, badgeWidth, 6, 2, 2, "F");
          pdf.doc.setFontSize(8);
          pdf.doc.setFont("helvetica", "bold");
          pdf.doc.setTextColor(statusColor);
          pdf.doc.text(statusText, badgeX + 4, badgeY + 4);
          pdf.doc.setFontSize(10); // Reset font size
          pdf.doc.setFont("helvetica", "normal"); // Reset font style
        } else {
          // Place badge on next line
          pdf.yPosition += pdf.lineHeight;
          addBadge(pdf, statusText, bgColor, statusColor);
          pdf.yPosition -= 3; // Adjust spacing
        }

        pdf.yPosition += 8;

        addKeyValue(pdf, "Submitted", formatDate(item.createdDate));

        if (item.estimatedCompletionDate) {
          addKeyValue(pdf, "Estimated Completion", formatDate(item.estimatedCompletionDate));
        }

        if (item.actualCompletionDate) {
          addKeyValue(pdf, "Completed", formatDate(item.actualCompletionDate));
        }

        if (item.modifiedDate) {
          addKeyValue(pdf, "Last Updated", formatDate(item.modifiedDate));
        }

        if (item.reason) {
          pdf.yPosition += 2;
          addText(pdf, "Reason:", 10, true);
          const reasonLines = pdf.doc.splitTextToSize(item.reason, pdf.pageWidth - 2 * pdf.margin);
          reasonLines.forEach((line: string) => {
            checkPageBreak(pdf, 10);
            addText(pdf, `  ${line}`, 9, false, "#666666");
          });
        }

        if (index < withdrawalHistory.data.length - 1) {
          addDivider(pdf);
        }
      });
    }

    // Footer
    addFooter(pdf, "This document is a summary of your consent history. For complete details, visit the online portal.");

    // Download the PDF
    const fileName = `purpose-consent-history-${purpose.purposeId}-${new Date().toISOString().split("T")[0]}.pdf`;
    savePDF(pdf, fileName);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "WITHDRAWN":
        return "error";
      case "EXPIRED":
        return "warning";
      case "LEGAL_RETENTION_ACTIVE":
        return "info";
      case "LEGAL_RETENTION_EXPIRED":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <CheckCircleIcon />;
      case "WITHDRAWN":
        return <BlockIcon />;
      case "EXPIRED":
        return <ScheduleIcon />;
      case "LEGAL_RETENTION_ACTIVE":
        return <GavelIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getConsentIcon = (consentValue: boolean) => {
    return consentValue ? <CheckCircleIcon color="success" /> : <BlockIcon color="error" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getWithdrawalStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "PENDING":
        return { color: "#ff9800", backgroundColor: "#fff3e0", icon: <PendingIcon /> };
      case "IN_PROGRESS":
        return { color: "#2196f3", backgroundColor: "#e3f2fd", icon: <AccessTimeIcon /> };
      case "COMPLETED":
        return { color: "#4caf50", backgroundColor: "#e8f5e9", icon: <CheckCircleOutlineIcon /> };
      case "CANCELLED":
        return { color: "#7b68ee", backgroundColor: "#f3f0ff", icon: <CancelIcon /> };
      case "REJECTED":
        return { color: "#f44336", backgroundColor: "#ffebee", icon: <ErrorIcon /> };
      default:
        return { color: "#757575", backgroundColor: "#f5f5f5", icon: <ScheduleIcon /> };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading purpose details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mt: 2 }}>
          Back to Purposes
        </Button>
      </Box>
    );
  }

  if (!purposeDetails || !purposeHistory) {
    return (
      <Box p={3}>
        <Alert severity="warning">Purpose not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mt: 2 }}>
          Back to Purposes
        </Button>
      </Box>
    );
  }

  const { history } = purposeHistory;
  const purpose = purposeDetails;

  return (
    <Box p={3}>
      {/* Header Section */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <IconButton
              onClick={handleBackClick}
              sx={{
                mr: 1,
                color: "var(--primary-green)",
                "&:hover": {
                  backgroundColor: "var(--bg-accent)",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: "var(--text-dark)",
                fontWeight: 700,
              }}
            >
              Purpose Details
            </Typography>
          </Box>

          <Tooltip title="Export consent history as PDF">
            <IconButton
              onClick={handleExportHistory}
              sx={{
                backgroundColor: "var(--bg-secondary)",
                color: "var(--text-dark)",
                border: "1px solid var(--border-light)",
                borderRadius: "var(--radius-sm)",
                "&:hover": {
                  backgroundColor: "var(--bg-accent)",
                  borderColor: "var(--primary-green)",
                  color: "var(--primary-green)",
                  transform: "translateY(-1px)",
                  boxShadow: "var(--shadow-light)",
                },
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Purpose Information */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              backgroundColor: "var(--bg-primary)",
              border: "1px solid var(--border-light)",
              borderRadius: "var(--radius-md)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              transition: "box-shadow 0.3s ease-in-out",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{
                    color: "var(--text-dark)",
                    fontWeight: 600,
                  }}
                >
                  {purpose.purposeName}
                </Typography>
                <Chip
                  icon={getStatusIcon(purpose.status)}
                  label={purpose.status.replace("_", " ")}
                  color={getStatusColor(purpose.status) as any}
                  sx={{
                    backgroundColor: purpose.status === "ACTIVE" ? "var(--bg-accent)" : undefined,
                    color: purpose.status === "ACTIVE" ? "var(--primary-green)" : undefined,
                    border: purpose.status === "ACTIVE" ? "1px solid var(--primary-green)" : undefined,
                  }}
                />
              </Box>

              {/* Withdrawal Request Status */}
              {purpose.withdrawalRequest && (
                <Box mb={2}>
                  <WithdrawalStatusChip withdrawalRequest={purpose.withdrawalRequest} onViewDetails={handleViewWithdrawalDetails} />
                </Box>
              )}

              <Typography variant="body2" mb={3} sx={{ color: "var(--text-medium)" }}>
                {purpose.purposeDescription}
              </Typography>

              <Divider
                sx={{
                  my: 2,
                  borderColor: "var(--border-light)",
                }}
              />

              {/* Purpose Details */}
              <List dense>
                <ListItem>
                  <ListItemText primary="Purpose Key" secondary={purpose.purposeKey} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Lawful Ground" secondary={purpose.lawfulGroundLabel} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Current Consent" secondary={purpose.currentConsent ? "Granted" : "Withdrawn"} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Last Updated" secondary={formatDate(purpose.lastConsentDate)} />
                </ListItem>
                {purpose.retentionExpirationDate && (
                  <ListItem>
                    <ListItemText primary="Retention Expires" secondary={formatDate(purpose.retentionExpirationDate)} />
                  </ListItem>
                )}
              </List>

              {/* Legal Retention Info */}
              {purpose.legalRetentionRequired && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    backgroundColor: "var(--bg-accent)",
                    border: "1px solid var(--primary-green)",
                    "& .MuiAlert-icon": {
                      color: "var(--primary-green)",
                    },
                    "& .MuiAlert-message": {
                      color: "var(--text-dark)",
                    },
                  }}
                >
                  <Typography variant="body2">
                    <strong>Legal Retention:</strong> This purpose has legal retention requirements.
                    {purpose.legalRetentionPeriod && ` Period: ${purpose.legalRetentionPeriod} days.`}
                  </Typography>
                </Alert>
              )}

              {/* End Conditions */}
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">End Conditions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="End on Purpose Fulfilled" secondary={purpose.endOnPurposeFulfilled ? "Yes" : "No"} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="End on Consent Withdrawn" secondary={purpose.endOnConsentWithdrawn ? "Yes" : "No"} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="End on Retention Expired" secondary={purpose.endOnRetentionExpired ? "Yes" : "No"} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="End on Erasure Requested" secondary={purpose.endOnErasureRequested ? "Yes" : "No"} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="End on Account Closure" secondary={purpose.endOnAccountClosure ? "Yes" : "No"} />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>

              {/* Action Buttons */}
              <Box mt={3} display="flex" gap={1} flexDirection="column">
                {purpose.canModify && (
                  <>
                    {purpose.currentConsent ? (
                      <Tooltip
                        title={
                          purpose.withdrawalRequest
                            ? `Withdrawal request already pending (Request #${purpose.withdrawalRequest.requestId})`
                            : "Withdraw consent for this purpose"
                        }
                        arrow
                      >
                        <span>
                          <Button
                            startIcon={<BlockIcon />}
                            onClick={handleWithdrawClick}
                            disabled={!!purpose.withdrawalRequest}
                            fullWidth
                            sx={{
                              backgroundColor: purpose.withdrawalRequest ? "var(--bg-disabled)" : "var(--warning-red-light)",
                              color: purpose.withdrawalRequest ? "var(--text-disabled)" : "var(--warning-text)",
                              border: purpose.withdrawalRequest ? "1px solid var(--border-disabled)" : "1px solid var(--warning-red)",
                              borderRadius: "var(--radius-md)",
                              fontWeight: 500,
                              padding: "var(--spacing-sm) var(--spacing-md)",
                              "&:hover": purpose.withdrawalRequest
                                ? {}
                                : {
                                    backgroundColor: "var(--warning-red)",
                                    borderColor: "var(--warning-red-dark)",
                                    color: "var(--text-white)",
                                    transform: "translateY(-2px)",
                                    boxShadow: "var(--shadow-medium)",
                                  },
                            }}
                          >
                            Withdraw Consent
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        startIcon={<CheckCircleIcon />}
                        onClick={handleGrantClick}
                        fullWidth
                        sx={{
                          backgroundColor: "var(--primary-green)",
                          color: "var(--text-white)",
                          border: "none",
                          borderRadius: "var(--radius-md)",
                          fontWeight: 600,
                          padding: "var(--spacing-sm) var(--spacing-md)",
                          "&:hover": {
                            backgroundColor: "var(--accent-brown)",
                            transform: "translateY(-2px)",
                            boxShadow: "var(--shadow-medium)",
                          },
                        }}
                      >
                        Grant Consent
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Consent History */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              backgroundColor: "var(--bg-primary)",
              border: "1px solid var(--border-light)",
              borderRadius: "var(--radius-md)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              transition: "box-shadow 0.3s ease-in-out",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    color: "var(--text-dark)",
                    fontWeight: 600,
                  }}
                >
                  Consent History
                </Typography>
                <Typography variant="body2" sx={{ color: "var(--text-medium)" }}>
                  {history.length} entries
                </Typography>
              </Box>

              {history.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <HistoryIcon
                    sx={{
                      fontSize: 48,
                      color: "var(--text-light)",
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" gutterBottom sx={{ color: "var(--text-medium)" }}>
                    No consent history found
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--text-light)" }}>
                    This purpose doesn't have any consent history yet.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {history.map((item, index) => (
                    <React.Fragment key={item.consentDetailId}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{
                          borderRadius: "var(--radius-sm)",
                          mb: 1,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            backgroundColor: "var(--bg-accent)",
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>{getConsentIcon(item.consentValue)}</ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {item.consentValue ? "Consent Granted" : "Consent Withdrawn"}
                                </Typography>
                                {item.isTest && (
                                  <Chip
                                    label="Sandbox"
                                    size="small"
                                    sx={{
                                      backgroundColor: "#fff3cd",
                                      color: "#856404",
                                      border: "1px solid #ffc107",
                                      fontWeight: 500,
                                      fontSize: "0.7rem",
                                      height: 22,
                                    }}
                                  />
                                )}
                                {(item.consentSource === "NOTICE" || item.consentSource === "NOTICE_OTP") && item.isMinor && (
                                  <Chip
                                    label="Guardian on behalf of minor"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ fontWeight: 500, fontSize: "0.7rem", height: 22 }}
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {formatDateShort(item.consentDate)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box position="relative" mt={1}>
                              <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={2}
                                mb={1.5}
                                sx={{
                                  "& > *": {
                                    flex: "1 1 200px",
                                    minWidth: 0,
                                  },
                                }}
                              >
                                {/* Collection Point Info */}
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                  >
                                    Collection Point
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.3 }}>
                                    {item.collectionPoint.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.collectionPoint.type} • {item.collectionPoint.identifier}
                                  </Typography>
                                </Box>

                                {/* Location and Device Info */}
                                {item.location && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                    >
                                      Location
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                                      <LocationIcon fontSize="small" color="action" />
                                      <Typography variant="body2">
                                        {item.location.city || 'N/A'}, {item.location.region || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {item.location.country || 'N/A'} • {item.ipAddress || 'N/A'}
                                    </Typography>
                                  </Box>
                                )}

                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                  >
                                    Language
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                                    <LanguageIcon fontSize="small" color="action" />
                                    <Typography variant="body2">{item.languageCode.toUpperCase()}</Typography>
                                  </Box>
                                </Box>

                                {/* Retention Info */}
                                {item.retentionExpirationDate && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                    >
                                      Retention Expires
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                                      {formatDate(item.retentionExpirationDate)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {/* User Agent */}
                              <Box
                                mt={1}
                                p={1}
                                sx={{
                                  backgroundColor: "var(--bg-secondary)",
                                  borderRadius: "var(--radius-sm)",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                >
                                  User Agent
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3, wordBreak: "break-word" }}>
                                  {item.userAgent}
                                </Typography>
                              </Box>

                              {/* View Details Button - Bottom Right */}
                              <Box position="absolute" bottom={0} right={0}>
                                <Tooltip title="View full consent record details">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/consent/${item.consentHeaderId}`);
                                    }}
                                    sx={{
                                      backgroundColor: "var(--bg-accent)",
                                      color: "var(--primary-green)",
                                      border: "1px solid var(--primary-green)",
                                      borderRadius: "var(--radius-sm)",
                                      "&:hover": {
                                        backgroundColor: "var(--primary-green)",
                                        color: "var(--text-white)",
                                        transform: "translateY(-1px)",
                                        boxShadow: "var(--shadow-light)",
                                      },
                                    }}
                                  >
                                    <ViewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < history.length - 1 && <Divider variant="inset" component="li" sx={{ borderColor: "var(--border-light)" }} />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Withdrawal Request History Section */}
          <Card
            sx={{
              mt: 3,
              backgroundColor: "var(--bg-primary)",
              border: "1px solid var(--border-light)",
              borderRadius: "var(--radius-md)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              transition: "box-shadow 0.3s ease-in-out",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    color: "var(--text-dark)",
                    fontWeight: 600,
                  }}
                >
                  Withdrawal Request History
                </Typography>
                <Typography variant="body2" sx={{ color: "var(--text-medium)" }}>
                  {withdrawalHistory?.data?.length || 0} request(s)
                </Typography>
              </Box>

              {!withdrawalHistory?.data || withdrawalHistory.data.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <BlockIcon
                    sx={{
                      fontSize: 48,
                      color: "var(--text-light)",
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" gutterBottom sx={{ color: "var(--text-medium)" }}>
                    No withdrawal requests found
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--text-light)" }}>
                    You haven't submitted any withdrawal requests for this purpose yet.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {withdrawalHistory.data.map((item, index) => {
                    const statusConfig = getWithdrawalStatusColor(item.requestStatus);
                    const isCancelled = item.requestStatus.toUpperCase() === "CANCELLED";
                    return (
                      <React.Fragment key={item.requestId}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{
                            backgroundColor: "transparent",
                            borderRadius: "var(--radius-sm)",
                            mb: 1,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: isCancelled ? "rgba(255, 82, 82, 0.08)" : "var(--bg-accent)",
                              transform: "translateX(4px)",
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {isCancelled ? (
                              <CancelIcon sx={{ color: "var(--warning-red)" }} />
                            ) : (
                              <Chip
                                icon={statusConfig.icon}
                                label={item.requestStatus}
                                size="small"
                                sx={{
                                  backgroundColor: statusConfig.backgroundColor,
                                  color: statusConfig.color,
                                  border: `1px solid ${statusConfig.color}`,
                                  fontWeight: 500,
                                  minWidth: 110,
                                  "& .MuiChip-icon": {
                                    color: statusConfig.color,
                                  },
                                }}
                              />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 600,
                                    color: "var(--text-dark)",
                                  }}
                                >
                                  Withdrawal Request #{item.requestId}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateShort(item.createdDate)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Box
                                  display="flex"
                                  flexWrap="wrap"
                                  gap={2}
                                  mb={1}
                                  sx={{
                                    "& > *": {
                                      flex: "1 1 200px",
                                      minWidth: 0,
                                    },
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                    >
                                      Status
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {item.requestStatus}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                    >
                                      Submitted
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {formatDate(item.createdDate)}
                                    </Typography>
                                  </Box>
                                  {item.estimatedCompletionDate && (
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                      >
                                        Estimated Completion
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {formatDate(item.estimatedCompletionDate)}
                                      </Typography>
                                    </Box>
                                  )}
                                  {item.actualCompletionDate && (
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                      >
                                        Completed
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {formatDate(item.actualCompletionDate)}
                                      </Typography>
                                    </Box>
                                  )}
                                  {item.modifiedDate && (
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "var(--text-light)", fontWeight: 600, textTransform: "uppercase", fontSize: "0.65rem" }}
                                      >
                                        Last Updated
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {formatDate(item.modifiedDate)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                {item.reason && (
                                  <Box
                                    mt={1}
                                    p={1.5}
                                    sx={{
                                      backgroundColor: "var(--bg-secondary)",
                                      borderRadius: "var(--radius-sm)",
                                      borderLeft: isCancelled ? `3px solid var(--warning-red)` : `3px solid ${statusConfig.color}`,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "var(--text-light)",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        fontSize: "0.65rem",
                                      }}
                                    >
                                      Reason
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">
                                      {item.reason}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < withdrawalHistory.data.length - 1 && (
                          <Divider variant="inset" component="li" sx={{ borderColor: "var(--border-light)" }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Withdrawal Confirmation Dialog */}
      {withdrawalDialog.purpose && (
        <WithdrawalConfirmationDialog
          open={withdrawalDialog.open}
          onClose={handleWithdrawalClose}
          onConfirm={handleWithdrawalConfirm}
          purposeName={withdrawalDialog.purpose.purposeName}
          purposeDescription={withdrawalDialog.purpose.purposeDescription}
          loading={withdrawalDialog.loading}
          error={withdrawalDialog.error || undefined}
        />
      )}
    </Box>
  );
};

export default PurposeDetailPage;
